/**
 * Inventory > Receive stock and Adjust count forms (tracker item 30).
 *
 * Both post through /api/inventory and report the server's answer; the
 * previews here are display arithmetic only (the server recomputes from the
 * locked on-hand). Rules and messages live in inventoryUi.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { IGLAccountsApi, IInventoryApi } from '../api';
import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { localToday } from './ledgerUi';
import {
  INVENTORY_TEXT_MAX_LENGTH,
  AdjustForm,
  AdjustMode,
  ReceiveForm,
  adjustBlocker,
  adjustPreview,
  adjustValueCents,
  buildAdjustRequest,
  buildReceiveRequest,
  defaultOffsetAccountId,
  emptyAdjustForm,
  emptyReceiveForm,
  formatQuantity,
  inventoryErrorMessage,
  inventoryMoney,
  inventoryMoneyFromCents,
  offsetAccountLabel,
  offsetAccountOptions,
  quantityFromHundredths,
  receiptValueCents,
  receiveBlocker,
  setReceiveUnitCost,
  wouldGoNegative,
} from './inventoryUi';
import { Toast } from '../components/Toast';

const panelClass = 'mb-6 rounded-lg border border-blue-200 bg-white p-5 shadow';

// ============ Receive stock ============

interface ReceiveStockFormProps {
  api: IInventoryApi & IGLAccountsApi;
  /** Items that can receive stock (active PRODUCT items) */
  items: types.InventoryValuationItem[];
  initialItemId: string;
  onDone: (result: types.InventoryChangeResponse) => void;
  onCancel: () => void;
}

export function ReceiveStockForm({ api, items, initialItemId, onDone, onCancel }: ReceiveStockFormProps) {
  const [form, setForm] = useState<ReceiveForm>(() => emptyReceiveForm(initialItemId, localToday()));
  const [accounts, setAccounts] = useState<types.GLAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api.listGLAccounts()
      .then((list) => {
        if (!active) return;
        setAccounts(list);
        // Default to 2000 Accounts Payable unless the user already picked one.
        setForm((current) => (current.offsetAccountId ? current : { ...current, offsetAccountId: defaultOffsetAccountId(list) }));
      })
      .catch((cause) => { if (active) setAccountsError(getErrorMessage(cause, 'Could not load the chart of accounts')); })
      .finally(() => { if (active) setAccountsLoading(false); });
    return () => { active = false; };
  }, [api]);

  const options = useMemo(() => offsetAccountOptions(accounts), [accounts]);
  const item = items.find((candidate) => candidate.itemId === form.itemId);
  const offset = options.find((account) => account.id === form.offsetAccountId);
  const blocker = receiveBlocker(form, item);
  const value = receiptValueCents(form, item);
  const costEntered = !!form.unitCost.trim();

  function update(patch: Partial<ReceiveForm>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (blocker || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.receiveStock(buildReceiveRequest(form));
      onDone(result);
    } catch (cause) {
      setError(inventoryErrorMessage(cause, 'Could not receive the stock'));
      setSubmitting(false);
    }
  }

  return (
    <section role="dialog" aria-modal="false" aria-labelledby="receive-title" className={panelClass}>
      <h2 id="receive-title" className="text-base font-semibold text-gray-900">Receive stock</h2>
      <p className="mt-1 text-sm text-gray-600">
        Adds the quantity to on-hand and posts Dr 1300 Inventory - Materials / Cr the offset account at quantity × cost.
      </p>
      <form className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit} noValidate>
        <div className="sm:col-span-2">
          <label htmlFor="receive-item" className={labelClass}>Item</label>
          <select id="receive-item" className={inputClass} value={form.itemId} disabled={submitting} onChange={(event) => update({ itemId: event.target.value })}>
            <option value="">Choose an item</option>
            {items.map((candidate) => <option key={candidate.itemId} value={candidate.itemId}>{candidate.name}</option>)}
          </select>
          {item && (
            <p className="mt-1 text-xs text-gray-500">
              On hand {formatQuantity(item.quantityOnHand)} {item.unitOfMeasure} · item cost {inventoryMoney(item.unitCost)}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="receive-quantity" className={labelClass}>Quantity received</label>
          <input id="receive-quantity" className={inputClass} inputMode="decimal" value={form.quantity} disabled={submitting}
            onChange={(event) => update({ quantity: event.target.value })} />
        </div>
        <div>
          <label htmlFor="receive-unit-cost" className={labelClass}>Unit cost (optional)</label>
          <input id="receive-unit-cost" className={inputClass} inputMode="decimal" value={form.unitCost} disabled={submitting}
            placeholder={item?.unitCost ? `Item cost ${inventoryMoney(item.unitCost)}` : 'Required: item has no cost'}
            onChange={(event) => setForm((current) => setReceiveUnitCost(current, event.target.value))} />
          {costEntered && (
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
              <input id="receive-update-cost" type="checkbox" checked={form.updateItemCost} disabled={submitting}
                onChange={(event) => update({ updateItemCost: event.target.checked })} />
              Also set the item&apos;s unit cost to this cost
            </label>
          )}
        </div>
        <div>
          <label htmlFor="receive-offset" className={labelClass}>Offset account (credited)</label>
          <select id="receive-offset" className={inputClass} value={form.offsetAccountId} disabled={submitting || accountsLoading}
            onChange={(event) => update({ offsetAccountId: event.target.value })}>
            <option value="">{accountsLoading ? 'Loading accounts…' : 'Choose an account'}</option>
            {options.map((account) => <option key={account.id} value={account.id}>{offsetAccountLabel(account)}</option>)}
          </select>
          {accountsError && <Toast kind="error">{accountsError}</Toast>}
        </div>
        <div>
          <label htmlFor="receive-date" className={labelClass}>Date received</label>
          <input id="receive-date" type="date" className={inputClass} value={form.adjustmentDate} disabled={submitting}
            onChange={(event) => update({ adjustmentDate: event.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="receive-memo" className={labelClass}>Memo (optional)</label>
          <textarea id="receive-memo" className={inputClass} rows={2} maxLength={INVENTORY_TEXT_MAX_LENGTH} value={form.memo} disabled={submitting}
            placeholder="e.g. supplier invoice number" onChange={(event) => update({ memo: event.target.value })} />
        </div>

        <div className="sm:col-span-2 text-sm text-gray-700 break-words" data-testid="receive-preview">
          {value !== null
            ? `Posts ${inventoryMoneyFromCents(value)}: Dr 1300 Inventory - Materials / Cr ${offset ? offsetAccountLabel(offset) : 'the offset account'}.`
            : 'Enter a quantity (and a cost if the item has none) to see the posting.'}
        </div>
        {blocker && <p className="sm:col-span-2 text-sm text-gray-600" data-testid="receive-blocker">{blocker}</p>}
        {error && <Toast kind="error">{error}</Toast>}
        <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row [&>button]:min-h-[44px] sm:[&>button]:min-h-0">
          <button type="submit" className={primaryButtonClass} disabled={!!blocker || submitting}>
            {submitting ? 'Receiving…' : 'Receive stock'}
          </button>
          <button type="button" className={secondaryButtonClass} disabled={submitting} onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </section>
  );
}

// ============ Adjust count ============

interface AdjustCountFormProps {
  api: IInventoryApi;
  item: types.InventoryValuationItem;
  onDone: (result: types.InventoryChangeResponse) => void;
  onCancel: () => void;
}

export function AdjustCountForm({ api, item, onDone, onCancel }: AdjustCountFormProps) {
  const [form, setForm] = useState<AdjustForm>(() => emptyAdjustForm(item.itemId, localToday()));
  /** The server answered NEGATIVE_STOCK (its locked on-hand differs from what this page loaded). */
  const [serverNegative, setServerNegative] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = adjustPreview(form, item.quantityOnHand);
  const confirmNegative = wouldGoNegative(preview) || serverNegative;
  const blocker = adjustBlocker(form, item.quantityOnHand, confirmNegative);
  const value = adjustValueCents(preview, item.unitCost);

  function update(patch: Partial<AdjustForm>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  /** A new quantity needs a fresh negative-stock confirmation (it applies to one value only). */
  function updateQuantity(patch: Partial<AdjustForm>) {
    setServerNegative(false);
    update({ ...patch, allowNegative: false });
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (blocker || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.adjustStock(buildAdjustRequest(form, confirmNegative));
      onDone(result);
    } catch (cause) {
      if (getErrorCode(cause) === 'NEGATIVE_STOCK') setServerNegative(true);
      setError(inventoryErrorMessage(cause, 'Could not adjust the count'));
      setSubmitting(false);
    }
  }

  const modeOption = (mode: AdjustMode, label: string) => (
    <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700 sm:min-h-0">
      <input type="radio" name="adjust-mode" id={`adjust-mode-${mode}`} value={mode} checked={form.mode === mode} disabled={submitting}
        onChange={() => updateQuantity({ mode })} />
      {label}
    </label>
  );

  let postingText: string;
  if (!preview || preview.changeHundredths === 0) postingText = '';
  else if (!item.unitCost || value === 0) postingText = 'The item has no unit cost: only the quantity changes, nothing is posted.';
  else if (value < 0) postingText = `Posts ${inventoryMoneyFromCents(-value)}: Dr 5000 Cost of Goods Sold / Cr 1300 Inventory - Materials (shrink).`;
  else postingText = `Posts ${inventoryMoneyFromCents(value)}: Dr 1300 Inventory - Materials / Cr 5000 Cost of Goods Sold (gain).`;

  return (
    <section role="dialog" aria-modal="false" aria-labelledby="adjust-title" className={panelClass}>
      <h2 id="adjust-title" className="text-base font-semibold text-gray-900">Adjust count: {item.name}</h2>
      <p className="mt-1 text-sm text-gray-600">
        On hand {formatQuantity(item.quantityOnHand)} {item.unitOfMeasure}. The difference is valued at the item cost ({inventoryMoney(item.unitCost)}).
      </p>
      <form className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit} noValidate>
        <fieldset className="sm:col-span-2 flex flex-wrap gap-6">
          <legend className="sr-only">Adjustment type</legend>
          {modeOption('count', 'Set new count')}
          {modeOption('change', 'Change by')}
        </fieldset>
        {form.mode === 'count' ? (
          <div>
            <label htmlFor="adjust-new-quantity" className={labelClass}>Counted quantity</label>
            <input id="adjust-new-quantity" className={inputClass} inputMode="decimal" value={form.newQuantity} disabled={submitting}
              onChange={(event) => updateQuantity({ newQuantity: event.target.value })} />
          </div>
        ) : (
          <div>
            <label htmlFor="adjust-change" className={labelClass}>Change (negative to remove)</label>
            <input id="adjust-change" className={inputClass} inputMode="decimal" value={form.quantityChange} disabled={submitting}
              placeholder="e.g. -3" onChange={(event) => updateQuantity({ quantityChange: event.target.value })} />
          </div>
        )}
        <div>
          <label htmlFor="adjust-date" className={labelClass}>Adjustment date</label>
          <input id="adjust-date" type="date" className={inputClass} value={form.adjustmentDate} disabled={submitting}
            onChange={(event) => update({ adjustmentDate: event.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="adjust-reason" className={labelClass}>Reason</label>
          <textarea id="adjust-reason" className={inputClass} rows={2} maxLength={INVENTORY_TEXT_MAX_LENGTH} value={form.reason} disabled={submitting}
            placeholder="e.g. cycle count, damaged, found in van" onChange={(event) => update({ reason: event.target.value })} />
        </div>

        {preview && (
          <div className="sm:col-span-2 text-sm text-gray-700" data-testid="adjust-preview">
            On hand {formatQuantity(item.quantityOnHand)} → {quantityFromHundredths(preview.resultHundredths)} (
            {preview.changeHundredths > 0 ? '+' : ''}{quantityFromHundredths(preview.changeHundredths)}). {postingText}
          </div>
        )}
        {confirmNegative && (
          <label className="sm:col-span-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <input id="adjust-allow-negative" type="checkbox" className="mt-0.5" checked={form.allowNegative} disabled={submitting}
              onChange={(event) => update({ allowNegative: event.target.checked })} />
            <span>This leaves a negative quantity on hand. Post it anyway (allow a negative quantity).</span>
          </label>
        )}
        {blocker && <p className="sm:col-span-2 text-sm text-gray-600" data-testid="adjust-blocker">{blocker}</p>}
        {error && <Toast kind="error">{error}</Toast>}
        <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row [&>button]:min-h-[44px] sm:[&>button]:min-h-0">
          <button type="submit" className={primaryButtonClass} disabled={!!blocker || submitting}>
            {submitting ? 'Adjusting…' : 'Adjust count'}
          </button>
          <button type="button" className={secondaryButtonClass} disabled={submitting} onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </section>
  );
}
